import {
    Button,
    Flex,
    Form,
    FormCheckbox,
    FormInput,
    FormRadio,
    FormSubmitHandler,
    ModalFooter
} from '@auviknetworks/iceberg-ui';
import { Box, Typography } from '@mui/material';
import { Group } from 'application/Shared/Components/Form/Group';
import { Notify, Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';
import { User as UserType } from 'application/Shared/types/user';
import { usePutUserSelf } from 'hooks/dataAccess/User/user';
import { useUpdateSession } from 'hooks/redux/Selectors/session';
import Session from 'objects/Session';
import { User, UserIcon } from 'objects/User';
import React from 'react';
import { Resolver, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import UserProfileImageChangeContainer from './UserProfileImageChangeContainer';

export interface Profile {
    firstName: string;
    lastName: string;
    theme: 'light' | 'dark' | 'system';
    title: string;
    tos?: boolean;
}

export interface ProfileFormProps {
    /** Flag indicating the form is shown for a first time user */
    initialSetup: boolean;
    /** User object containing default form values */
    user: UserType;
}

/**
 * Footer component for the user configuration form that displays action buttons.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} [props.initialSetup] - Flag indicating if this is the initial user setup
 *
 * @returns {JSX.Element} A footer component containing a submit button
 */
const Footer = ({ initialSetup }: Partial<ProfileFormProps>) => {
    const { t } = useTranslation();
    const {
        formState: { isDirty }
    } = useFormContext();

    return (
        <ModalFooter>
            <Button
                data-trackid="configureSelfUser_save"
                data-testid="configureSelfUserSaveBtn"
                type="submit"
                size="medium"
                // There seems to be an issue with FormCheckbox where it doesn't flip the form's dirty state
                // when checked/unchecked. Since we only display the tos checkbox on initial setup, we'll just
                // not disable it there.
                disabled={!initialSetup && !isDirty}
            >
                {t(
                    initialSetup
                        ? 'SetupUser.save_and_continue'
                        : 'Common.update'
                )}
            </Button>
        </ModalFooter>
    );
};

/**
 * A form component for managing user profile information.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.initialSetup - Flag indicating if this is the initial user setup flow
 * @param {Object} props.user - User object containing default form values
 *
 * @returns {JSX.Element} A form component for editing user profile information
 */
export const ProfileForm = ({ initialSetup, user }: ProfileFormProps) => {
    const { t } = useTranslation();

    const updateSession = useUpdateSession();
    const { putUserSelf } = usePutUserSelf();

    const validate: Resolver<Profile> = async (values: Profile) => {
        const errors: Record<string, { type: string; message: string }> = {};

        if (!values.firstName.trim()) {
            errors.firstName = {
                message: t('ConfigureSelfUser.error.first_name'),
                type: 'required'
            };
        }
        if (!values.lastName.trim()) {
            errors.lastName = {
                message: t('ConfigureSelfUser.error.last_name'),
                type: 'required'
            };
        }
        if (!values.tos && initialSetup) {
            errors.tos = {
                message: t('SetupUser.error_accept_tos'),
                type: 'required'
            };
        }

        return { errors, values };
    };

    const onSubmit: FormSubmitHandler<Profile> = async (
        values,
        _,
        formMethods
    ) => {
        const params: Profile = {
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            theme: values.theme,
            title: values.title.trim()
        };

        if (initialSetup) {
            params.tos = values.tos;
        }

        try {
            const response = await putUserSelf({ params });
            if (
                Response.hasData(response) &&
                Response.isSuccessCode(response.status)
            ) {
                const userSession = await Session.getSessionInfo();
                updateSession(userSession);
                Notify.success(t('ConfigureSelfUser.success_saving'));
                if (formMethods) {
                    formMethods.reset(values);
                }
            }
        } catch (err) {
            debug.e('ConfigureSelfUser handleSubmit failed', err);
            Notify.e(t('ConfigureSelfUser.error_saving'));
        }
    };

    return (
        <>
            <Box sx={{ margin: '24px' }}>
                <Group title={t('Profile.avatar')}>
                    <User data={user} key={user.dateUpdated} id={user.id}>
                        <Flex alignItems="center" sx={{ gap: '16px' }}>
                            <UserIcon
                                noLink
                                size="md"
                                avatarGroup={false}
                                noFloat={false}
                                style={{}}
                            />
                            <UserProfileImageChangeContainer />
                        </Flex>
                    </User>
                </Group>
            </Box>

            <Form<Profile>
                defaultValues={user as Profile}
                onSubmit={onSubmit}
                validate={validate}
                mode="onChange"
            >
                <Box sx={{ margin: '24px' }}>
                    <Group title={t('Profile.details')}>
                        <FormInput
                            name="email"
                            disabled
                            required
                            label={t('Common.email')}
                        />

                        <FormInput
                            name="firstName"
                            required
                            // We only focus when in initial setup
                            autoFocus={initialSetup}
                            label={t('Common.first_name')}
                        />

                        <FormInput
                            name="lastName"
                            label={t('Common.last_name')}
                            required
                        />

                        <FormInput name="title" label={t('Common.job_title')} />
                    </Group>
                </Box>

                <Box sx={{ margin: '24px' }}>
                    <Group title={t('Profile.theme')}>
                        {['light', 'dark', 'system'].map((setting) => (
                            <FormRadio
                                key={setting}
                                name="theme"
                                value={setting}
                            >
                                {t(`Theme.${setting}`)}
                            </FormRadio>
                        ))}
                    </Group>
                </Box>

                {initialSetup && (
                    <Box sx={{ margin: '24px' }}>
                        <Group title={t('SetupUser.tos')}>
                            <FormCheckbox
                                label={
                                    <Typography
                                        variant="body1"
                                        dangerouslySetInnerHTML={{
                                            __html: t('SetupUser.accept_tos')
                                        }}
                                    />
                                }
                                name="tos"
                            />
                        </Group>
                    </Box>
                )}
                <Footer initialSetup={initialSetup} />
            </Form>
        </>
    );
};
